import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  EyeOff, KeyRound, Fingerprint, ServerCog, Lock, ShieldCheck,
  History, Building2, Gauge, FileLock2, DatabaseBackup, ScanLine,
  Laptop, FileWarning, UserRoundX, BookmarkCheck, Mail, ArrowLeft,
} from 'lucide-react';

// Central de Segurança e Privacidade — página pública (/seguranca).
//
// Design casado com a LandingPageV2: fundo branco/#F3F3F3, texto #0B2942,
// accent #009EDB, seções escuras #031B31, cantos rounded-3xl, fonte Mulish/Inter.
// O gradiente ciano→violeta vem da marca nova (logo REPORTFius).
//
// REGRA DESTA PÁGINA: só afirmações VERIFICÁVEIS no código. Uma versão anterior
// publicava um "relatório" com nota 9.7/10, "todas as vulnerabilidades
// corrigidas" e rotação de chaves que não existia mais. Publicar postura de
// segurança factualmente errada, num produto de compliance, é exposição
// jurídica. Cada item abaixo tem contrapartida no backend — a referência está
// no comentário ao lado. Antes de acrescentar qualquer linha aqui, confirme no
// código; se não der para apontar onde está implementado, não entra.

const BRAND_GRADIENT = 'linear-gradient(135deg, #00C2F0 0%, #1E7BF0 45%, #9B30E0 100%)';

const img = (name) => `${process.env.PUBLIC_URL || ''}/images/brand/${name}`;

const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const SectionTitle = ({ eyebrow, title, children, dark = false }) => (
  <div className="mx-auto mb-12 max-w-3xl text-center">
    {eyebrow && (
      <span
        className={`mb-4 inline-block text-xs font-bold uppercase tracking-[0.18em] ${
          dark ? 'text-[#5BC5EE]' : 'text-[#009EDB]'
        }`}
      >
        {eyebrow}
      </span>
    )}
    <h2
      className={`text-3xl font-black tracking-tight sm:text-4xl ${
        dark ? 'text-white' : 'text-[#0B2942]'
      }`}
    >
      {title}
    </h2>
    {children && (
      <p
        className={`mt-4 text-base leading-relaxed ${
          dark ? 'text-white/70' : 'text-[#0B2942]/70'
        }`}
      >
        {children}
      </p>
    )}
  </div>
);

/* ------------------------------------------------------------------ *
 * Conteúdo — cada item aponta para onde está implementado no backend
 * ------------------------------------------------------------------ */

// backend/middleware/audit.js (ROTAS_ANONIMAS) · routes/complaints.js
const ANONIMATO = [
  {
    icon: UserRoundX,
    title: 'Denúncia sem cadastro',
    text: 'Você não precisa criar conta, informar e-mail nem se identificar para registrar uma denúncia. O formulário público não exige login.',
  },
  {
    icon: Fingerprint,
    title: 'Sem registro do seu IP',
    text: 'A denúncia não guarda seu endereço de IP. Nas páginas públicas de envio e acompanhamento, o log de acesso também não registra IP, navegador nem dispositivo — justamente para que ninguém possa cruzar horário de acesso com denúncia e chegar até você.',
  },
  {
    icon: KeyRound,
    title: 'Código gerado pelo servidor',
    text: 'O código de acompanhamento é gerado no servidor, aleatório e único. Ele não deriva do seu nome, do horário nem de qualquer dado seu — não há como adivinhá-lo ou reconstruí-lo a partir da denúncia.',
  },
  {
    icon: EyeOff,
    title: 'Você controla o que revela',
    text: 'Identificar-se é opcional e é sempre uma escolha sua. Se preferir criar um usuário anônimo para acompanhar o caso, a senha escolhida é guardada apenas como hash — nem a equipe do sistema consegue lê-la.',
  },
];

// Cada linha: onde está no backend, para auditoria futura desta página.
const CAMADAS = [
  {
    icon: Lock,
    title: 'Tráfego criptografado',
    text: 'Todo o acesso acontece por HTTPS/TLS, com HSTS habilitado por um ano — o navegador se recusa a voltar para uma conexão não segura.',
    // server.js: helmet hsts { maxAge: 31536000, includeSubDomains, preload }
  },
  {
    icon: ShieldCheck,
    title: 'Senhas nunca em texto puro',
    text: 'Senhas são guardadas apenas como hash bcrypt com fator de custo elevado. Não há como recuperá-las a partir do banco — só redefinir.',
    // routes/*.js: bcrypt.hash(password, 12)
  },
  {
    icon: ScanLine,
    title: 'Verificação em duas etapas',
    text: 'Contas administrativas podem exigir um segundo fator: código de uso único enviado por e-mail, válido por poucos minutos, com limite de tentativas. Se o envio falha, o login não passa.',
    // services/twoFactor.js: VALIDADE_MS, MAX_TENTATIVAS, falha fechada
  },
  {
    icon: Gauge,
    title: 'Barreira contra força bruta',
    text: 'O login tem limite de tentativas por origem e por conta. O formulário público e a redefinição de senha têm limites próprios, para evitar enxurrada de envios ou bombardeio de e-mails.',
    // middleware/loginRateLimit.js · routes/complaints.js publicFormLimiter
  },
  {
    icon: History,
    title: 'Trilha de auditoria',
    text: 'As ações no painel ficam registradas em uma trilha de auditoria com data, autor e operação, para que o tratamento de cada denúncia seja acompanhável e responsável.',
    // server.js: app.use(auditMiddleware) · routes/audit.js
  },
  {
    icon: Building2,
    title: 'Isolamento por organização',
    text: 'Cada organização enxerga somente as próprias denúncias. A separação é aplicada no servidor, a cada requisição — não depende do que a tela pede.',
    // middleware/auth.js: client_slug em toda consulta autenticada
  },
  {
    icon: ServerCog,
    title: 'Sessão com prazo e assinatura fixa',
    text: 'A sessão é assinada com algoritmo fixado no servidor e expira. Um token com algoritmo trocado é rejeitado, e não há sessão que valha para sempre.',
    // server.js: jwt.verify(..., { algorithms: ['HS256'] }) · JWT_EXPIRES_IN
  },
  {
    icon: FileLock2,
    title: 'Anexos controlados',
    text: 'Só passam tipos de arquivo previstos em lista fechada, com limite de tamanho e nome higienizado antes de gravar. O download exige autenticação.',
    // routes/files.js e routes/complaints.js: fileFilter + limits
  },
  {
    icon: DatabaseBackup,
    title: 'Backups cifrados',
    text: 'Os backups automáticos são cifrados com AES-256-GCM em memória e gravados já criptografados. Sem a chave, o backup não é gerado — nunca é escrito em texto puro.',
    // services/backupEncryption.js: encryptBackupBuffer, aborta sem chave
  },
  {
    icon: Laptop,
    title: 'Navegador em modo restrito',
    text: 'A plataforma envia uma política de conteúdo restritiva: não pode ser embutida em outro site, não carrega script de terceiros e bloqueia acesso a câmera, microfone e localização.',
    // server.js: helmet CSP frame-ancestors none · permissionsPolicy
  },
  {
    icon: FileWarning,
    title: 'Logs sem dado sensível',
    text: 'Os registros técnicos passam por mascaramento antes de serem gravados, para que credenciais e dados sensíveis não vazem por log de aplicação.',
    // services/auditCrypto.js: maskSensitiveLogData
  },
  {
    icon: BookmarkCheck,
    title: 'Acesso mínimo necessário',
    text: 'O perfil de cada pessoa define exatamente o que ela alcança. Quem trata um caso não recebe acesso amplo à base por padrão.',
    // middleware/auth.js: requireClientAdmin, admin_levels, perfis
  },
];

// Orientações ao denunciante. Não são promessas do sistema — são riscos que
// estão FORA do nosso alcance e que ele precisa conhecer para se proteger.
const CUIDADOS = [
  [
    'Prefira um dispositivo e uma rede pessoais',
    'Equipamentos e redes corporativas podem ser monitorados pelo empregador. Nossa plataforma não registra seu IP, mas a rede por onde você navega pode registrar o acesso.',
  ],
  [
    'Guarde o código de acompanhamento',
    'É ele que dá acesso ao andamento do caso. Anote em local seguro, fora do computador da empresa.',
  ],
  [
    'Cuidado com o que o relato revela',
    'Detalhes muito específicos podem identificar quem escreveu, mesmo sem nome. Conte o necessário para a apuração.',
  ],
  [
    'Arquivos carregam metadados',
    'Fotos podem guardar local e aparelho; documentos do Office e PDFs guardam autor e histórico. Se o anonimato é essencial, limpe os metadados ou envie o conteúdo como texto.',
  ],
];

const SecurityPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white font-[Mulish,Inter,system-ui,sans-serif] text-[#0B2942] antialiased">
      {/* ============================== HEADER ============================== */}
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 text-left transition hover:opacity-80"
          >
            <img
              src={img('reportfius-icon-light-128.png')}
              alt="REPORTFius"
              className="h-10 w-10 shrink-0"
            />
            <span>
              <span className="block text-sm font-extrabold leading-tight text-[#0B2942] sm:text-base">
                Central de Segurança e Privacidade
              </span>
              <span className="block text-xs leading-tight text-[#0B2942]/55 sm:text-sm">
                REPORTFius — Canal de Denúncias
              </span>
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B2942]/60 transition-colors hover:text-[#009EDB]"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Início
          </button>
        </div>
      </header>

      {/* =============================== HERO =============================== */}
      <section className="relative overflow-hidden bg-[#031B31]">
        {/* marca d'água do símbolo */}
        <img
          src={img('reportfius-icon-dark-512.png')}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-24 hidden w-[520px] opacity-[0.07] lg:block"
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-1"
          style={{ background: BRAND_GRADIENT }}
        />

        <div className="relative mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <span
              className="mb-6 inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white"
              style={{ background: BRAND_GRADIENT }}
            >
              Proteção ao denunciante em primeiro lugar
            </span>
            <h1 className="text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sua denúncia,{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: BRAND_GRADIENT }}
              >
                protegida
              </span>
              .
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75">
              Quem denuncia assume um risco real. Por isso não basta dizer que a
              plataforma é segura — é preciso mostrar como. Abaixo está, de forma
              concreta, o que fazemos para proteger sua identidade e o que você
              pode fazer para se proteger ainda mais.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============================= ANONIMATO ============================= */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal>
            <SectionTitle eyebrow="O ponto mais importante" title="Como protegemos seu anonimato">
              O anonimato só vale se resistir a quem tem acesso ao sistema por
              dentro. Estas são as decisões técnicas que sustentam isso.
            </SectionTitle>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {ANONIMATO.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="h-full rounded-3xl bg-[#F3F3F3] p-8 transition hover:bg-white hover:shadow-xl hover:ring-1 hover:ring-black/5">
                  <div
                    className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl text-white"
                    style={{ background: BRAND_GRADIENT }}
                  >
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="mb-2 text-lg font-extrabold text-[#0B2942]">{title}</h3>
                  <p className="text-sm leading-relaxed text-[#0B2942]/70">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== CAMADAS ============================== */}
      <section className="bg-[#F3F3F3] py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal>
            <SectionTitle eyebrow="Camadas de proteção" title="O que roda por baixo">
              Segurança não é um recurso, é um conjunto de camadas. Se uma falha,
              as outras precisam continuar de pé.
            </SectionTitle>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAMADAS.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-2xl bg-white p-6 ring-1 ring-black/5 transition hover:shadow-lg">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#009EDB]/10 text-[#009EDB]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h3 className="mb-2 text-base font-extrabold text-[#0B2942]">{title}</h3>
                  <p className="text-sm leading-relaxed text-[#0B2942]/65">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== CUIDADOS ============================== */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal>
            <SectionTitle eyebrow="Fora do nosso alcance" title="O que depende de você">
              Há riscos que nenhuma plataforma consegue eliminar por você. Ser
              honesto sobre eles é parte de proteger quem denuncia.
            </SectionTitle>
          </Reveal>

          <div className="mx-auto max-w-4xl divide-y divide-black/5 overflow-hidden rounded-3xl ring-1 ring-black/5">
            {CUIDADOS.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="flex gap-5 bg-white p-7">
                  <span
                    className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                    style={{ background: BRAND_GRADIENT }}
                  >
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="mb-1.5 font-extrabold text-[#0B2942]">{title}</h3>
                    <p className="text-sm leading-relaxed text-[#0B2942]/70">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ COMPROMISSO ============================ */}
      <section className="mx-auto max-w-6xl px-5 pb-20 lg:px-8 lg:pb-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-[#031B31] px-8 py-14 text-white sm:px-12">
            <div
              className="absolute inset-x-0 top-0 h-1"
              style={{ background: BRAND_GRADIENT }}
            />
            <h2 className="mb-4 text-2xl font-extrabold sm:text-3xl">
              Segurança é um processo contínuo
            </h2>
            <p className="max-w-3xl leading-relaxed text-white/80">
              Não acreditamos em selos de perfeição. Nenhum sistema é imune, e quem
              promete o contrário não está sendo honesto. Você não vai encontrar
              aqui nota de auditoria nem lista de vulnerabilidades zeradas. O que
              assumimos com você é o compromisso de revisar a plataforma de forma
              independente, corrigir o que for encontrado com prioridade e tratar a
              proteção do denunciante como o centro de tudo — não como um detalhe.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ============================= DISCLOSURE ============================= */}
      <section className="bg-[#F3F3F3] py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Reveal>
            <div
              className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl text-white"
              style={{ background: BRAND_GRADIENT }}
            >
              <ShieldCheck className="h-7 w-7" aria-hidden="true" />
            </div>
            <h2 className="mb-3 text-2xl font-extrabold text-[#0B2942] sm:text-3xl">
              Encontrou uma falha? Conte pra gente.
            </h2>
            <p className="mx-auto mb-8 max-w-2xl leading-relaxed text-[#0B2942]/70">
              Levamos a sério cada relato de segurança. Se você identificou uma
              possível vulnerabilidade, entre em contato de forma responsável —
              analisamos e respondemos com seriedade. Pedimos apenas que não
              acesse dados de terceiros nem divulgue a falha antes da correção.
            </p>
            <a
              href="mailto:suporte@reportfius.com.br"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-semibold text-white shadow-lg transition hover:opacity-90"
              style={{ background: BRAND_GRADIENT }}
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              suporte@reportfius.com.br
            </a>
          </Reveal>
        </div>
      </section>

      {/* =============================== FOOTER =============================== */}
      <footer className="bg-[#031B31] py-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-5 text-center lg:px-8">
          <img
            src={img('reportfius-logo-dark-128.png')}
            alt="REPORTFius"
            className="h-16 w-auto"
          />
          <p className="text-sm text-white/55">
            Canal de Denúncias · Compromisso com a privacidade e a LGPD
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-sm font-semibold text-white/70 transition hover:text-white"
          >
            ← Voltar ao início
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SecurityPage;
